'use client';

import Link from 'next/link';
import { useCallback, useEffect, useRef, useState } from 'react';

import { routes } from '@endaoment-frontend/routes/server';

import { cn } from '../../cn';
import { button } from '../../recipes/button';
import { ArrowIcon } from '../ArrowIcon';
import { Button } from '../Button';
import { CloseIcon, MenuIcon } from '../MenuIcons';

import { BLOG_NAV, NAV_LINKS } from './PrimaryNav';

/* `closing` keeps the panel mounted for its exit; its own animationend takes
   it to `closed`. */
type State = 'closed' | 'closing' | 'open';

/**
 * The header's nav below lg.
 *
 * A disclosure, not a dialog: focus stays on the button when the panel opens,
 * so someone can tab straight into the links or straight past them. Escape
 * closes it and hands focus back.
 */
export const MobileNav = () => {
  const [state, setState] = useState<State>('closed');
  const open = state === 'open';
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);

  /* Straight to closed under reduced motion: the exit animation is off there,
     so there is no end to wait for and `closing` would never leave. The
     typeof is for jsdom, which has no matchMedia. */
  const close = useCallback(() => {
    const still =
      typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    setState(still ? 'closed' : 'closing');
  }, []);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      close();
      /* Back to the control that opened it. Left where it was, focus would be
         on a link that is no longer on the page. */
      trigger.current?.focus();
    };
    /* Pointer, not click: a press that starts outside should close the panel
       even if it ends somewhere else. */
    const onPointerDown = (event: PointerEvent) => {
      if (root.current?.contains(event.target as Node) === true) return;
      close();
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open, close]);

  /* Each row's place in the list, for the stagger. The sections and the
     button count on from the four links, so the whole sheet is one cascade. */
  let row = 0;
  const rowStyle = () => ({ '--row': row++ }) as React.CSSProperties;

  return (
    /* data-menu-open is what the header reads to take the panel's ground while
       it is open: StickyHeader cannot see this state, and :has() can. It stays
       through the exit, so the bar's ground goes with the panel's. */
    <div ref={root} className='lg:hidden' data-menu-open={state === 'closed' ? undefined : ''}>
      <Button
        ref={trigger}
        intent='ghost'
        size='sm'
        aria-expanded={open}
        aria-controls='mobile-nav'
        onClick={() => (open ? close() : setState('open'))}
        /* The negative margin hangs the button's padding off the gutter, so
           its icon's right edge lands where the panel's arrows end. */
        className='-mr-3 px-3 text-slate-700'>
        <span className='sr-only'>{open ? 'Close menu' : 'Open menu'}</span>
        {/* Both icons in one box, and the open one turns into the other. */}
        <span aria-hidden className='relative block size-6'>
          <MenuIcon
            className={cn(
              'absolute inset-0 transition-[opacity,rotate] duration-200 ease-out motion-reduce:transition-none',
              open && 'rotate-90 opacity-0',
            )}
          />
          <CloseIcon
            className={cn(
              'absolute inset-0 transition-[opacity,rotate] duration-200 ease-out motion-reduce:transition-none',
              !open && '-rotate-90 opacity-0',
            )}
          />
        </span>
      </Button>

      {/* A named nav landmark. The header already has one, and two unnamed navs
          are two entries a screen reader cannot tell apart.

          The same slate-50 the header takes while this is open, with a rule
          the whole width between them. The floor is a step darker and two
          pixels, the rules between rows a step lighter and one, so the edge
          the page shows under reads as the panel's and not as another row.

          animationend bubbles from the rows, so only the panel's own end
          counts, or the first row's entrance would close a panel on its way
          in. */}
      <nav
        id='mobile-nav'
        aria-label='Site'
        hidden={state === 'closed'}
        onAnimationEnd={event => {
          if (event.target === event.currentTarget && state === 'closing') setState('closed');
        }}
        className={cn(
          'absolute inset-x-0 top-nav max-h-[calc(100dvh-var(--spacing-nav))] overflow-y-auto overscroll-contain border-t border-b-2 border-slate-200 border-b-slate-300 bg-slate-50 px-gutter pb-gutter',
          state === 'closing' ? 'nav-panel-out' : 'nav-panel-in',
        )}>
        <ul className='m-0 flex list-none flex-col divide-y divide-slate-200 p-0'>
          {NAV_LINKS.map(link => (
            <li key={link.href} className='nav-row-in' style={rowStyle()}>
              <Link href={link.href} onClick={close} className={ROW}>
                {link.label}
                {/* The arrow in the page's own hue, the one the desktop nav
                    lights the segment in. */}
                <ArrowIcon className={cn(ARROW, link.current)} />
              </Link>
            </li>
          ))}
          <li className='nav-row-in' style={rowStyle()}>
            <Link href={BLOG_NAV.href} onClick={close} className={ROW}>
              {BLOG_NAV.label}
              <ArrowIcon className={cn(ARROW, BLOG_NAV.current)} />
            </Link>
            {/* Indented a step: the section icons already mark these as the
                row above's, and a rem left the labels short of the arrows at
                the far end. */}
            <ul className='m-0 flex list-none flex-col divide-y divide-slate-200 border-t border-slate-200 p-0 pl-2'>
              {BLOG_NAV.sections.map(section => (
                <li key={section.href} className='nav-row-in' style={rowStyle()}>
                  <Link
                    href={section.href}
                    onClick={close}
                    className='title-7 flex items-center gap-2.5 py-3 text-slate-700'>
                    <section.Icon className='size-5 shrink-0 text-scarlet-700' />
                    {section.label}
                    <ArrowIcon className={cn(ARROW, 'text-scarlet-300')} />
                  </Link>
                </li>
              ))}
            </ul>
          </li>
          <li className='nav-row-in' style={rowStyle()}>
            <Link
              href={routes.app.home()}
              data-cta='sign_in'
              data-cta-placement='mobile_nav'
              onClick={close}
              className={ROW}>
              Sign In
              <ArrowIcon className={ARROW} />
            </Link>
          </li>
          <li className='nav-row-in pt-4' style={rowStyle()}>
            <Link
              href={routes.app.home()}
              data-cta='open_daf'
              data-cta-placement='mobile_nav'
              onClick={close}
              className={button({ size: 'sm', block: true })}>
              Open a DAF
            </Link>
          </li>
        </ul>
      </nav>
    </div>
  );
};

/* A full-width row: the label at one end, the arrow at the other. */
const ROW = 'title-4 flex items-center justify-between gap-4 py-3.5 text-slate-950';
/* Half strength: at full hue eight of them in a column outweighed the labels. */
const ARROW = 'ml-auto w-5 shrink-0 opacity-50';
